import { useModal } from '@ui-components/neo.tax';
import { useMemo, useState } from 'react';

import { FilterSortDataType, OperatorName, SelectedRdProjectCategory } from '@neo.tax/shared';

import { useRdProjects, type RdProject } from './useRdProjects';
import { useWeightedRdPercentageToExpenses } from './useWeightedRdPercentageToExpenses';

interface UseRdPercentageBulkApplyProps {
  fiscalYearId: string;
  loading: boolean;
  filter?: Record<string, unknown>;
}

interface UseRdPercentageBulkApplyReturn {
  rdProjects: RdProject[] | null;
  hasRdEligibleProjects: boolean;
  selectedRowIds: string[];
  setSelectedRows: (ids: string[]) => void;
  isAllSelected: boolean;
  setIsAllSelected: (isAllSelected: boolean) => void;
  isApplyRDPercentageModalOpen: boolean;
  setIsApplyRDPercentageModalOpen: (isOpen: boolean) => void;
  disableApplyRDPercentageToExpensesButton: boolean;
  submit: (allocationPercent: number) => Promise<void>;
}

export const useRdPercentageBulkApply = ({
  filter,
  fiscalYearId,
  loading,
}: UseRdPercentageBulkApplyProps): UseRdPercentageBulkApplyReturn => {
  const { rdProjects } = useRdProjects({
    filterConditions: {
      booleanOperator: 'AND',
      conditionRows: [
        {
          id: 'rd-eligible-filter',
          column: 'selectedCategory',
          dataType: FilterSortDataType.ENUM,
          operator: OperatorName.IS,
          value: SelectedRdProjectCategory.RdCredit,
        },
      ],
    },
    numElementsInitialFetch: 1,
  });

  const [selectedRowIds, setSelectedRows] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const { isOpen: isApplyRDPercentageModalOpen, setIsOpen: setIsApplyRDPercentageModalOpen } = useModal();

  const closeApplyRDPercentageModal = (): void => {
    setIsApplyRDPercentageModalOpen(false);
  };

  const { applyWeightedRdPercentageToExpenses } = useWeightedRdPercentageToExpenses({
    fiscalYearId,
    onSuccess: closeApplyRDPercentageModal,
  });

  const hasRdEligibleProjects = !!(rdProjects && rdProjects.length > 0);

  const submit = async (allocationPercent: number): Promise<void> => {
    if (isAllSelected) {
      await applyWeightedRdPercentageToExpenses({
        desiredRdPercentage: allocationPercent,
        applyToAll: true,
        filter,
      });
    } else {
      await applyWeightedRdPercentageToExpenses({
        rdExpenseIds: selectedRowIds,
        desiredRdPercentage: allocationPercent,
      });
    }
  };

  const disableApplyRDPercentageToExpensesButton = useMemo(
    () => (!isAllSelected && selectedRowIds.length < 1) || loading,
    [isAllSelected, selectedRowIds, loading],
  );

  return {
    rdProjects,
    hasRdEligibleProjects,
    selectedRowIds,
    setSelectedRows,
    isAllSelected,
    setIsAllSelected,
    isApplyRDPercentageModalOpen,
    setIsApplyRDPercentageModalOpen,
    disableApplyRDPercentageToExpensesButton,
    submit,
  };
};
